import API from '@core/api.js'
import currency from 'currency.js'

import { useHttp } from '@core/composables/useHttp'
import {
  buildFormDataFrom,
  removeNullableFieldsFrom,
} from '@core/utils/helpers'
import { ref, computed } from 'vue'
import { camelToSnake } from '@core/utils/helpers'
import { OrderTypes } from '@modules/app-steps/step-two/tabs/tabs-data'
import { useRouteParams } from '@vueuse/router'
import {
  activeStep,
  orderType,
} from '@/components/modules/app-steps/step-two/index.state'
import { useOrderStore } from '@/stores/order'
import { useTabsStore } from '../../../stores/tabs'

//////////////////////////////////////////////////////////////////////////
//// State

const order = ref(null)

const orderTotalPrice = computed(() => {
  return currency(order?.value?.total).format()
})

const redeemedOrderWithZeroTotal = computed(() => {
  return (
    order?.value?.orderType === OrderTypes.RedeemedCredit &&
    order?.value?.total === 0
  )
})

const zeroTotal = computed(() => order?.value?.total === 0)

//////////////////////////////////////////////////////////////////////////
//// Composable

const useOrder = () => {
  const orderStore = useOrderStore()
  const tabsStore = useTabsStore()
  const getOrderId = () => {
    const orderId = useRouteParams('id')
    return orderId.value ?? ''
  }

  const getAllOrders = ({ params = {} } = {}) => {
    const orders = ref([])

    const { isLoading, execute } = useHttp()

    execute(
      { params, url: API.getOrdersResource },
      ({ data }) => (orders.value = data)
    )

    return { orders, isLoading }
  }

  const getLastIncompleteOrder = () => {
    const order = ref(null)

    const { isLoading, execute } = useHttp({
      method: 'GET',
      showErrorToast: false,
    })

    const packageName = computed(
      () => order?.value?.packagePrice?.package?.name
    )

    execute(
      { url: API.lastIncompleteOrderResource },
      (result) => (order.value = result),
      () => (order.value = null)
    )

    return { order, isLoading, packageName }
  }

  const getOrderById = (orderId = getOrderId()) => {
    order.value = null
    orderStore.clearOrder()
    // tabsStore.backTabsToInitialState()

    const { data, isReady, isLoading, execute } = useHttp()

    execute(
      {
        url: API.getOrderResource.replace(':id', orderId),
      },
      () => {
        order.value = data.value
        orderStore.setOrder(data.value)
        orderStore.setOrderType(data.value.orderType)
      }
    )

    return { isLoading, isReady, order }
  }

  const useUpdateOrder = (orderId = getOrderId()) => {
    const uploadingProgress = ref(0)

    const onUploadProgress = (progressEvent) => {
      uploadingProgress.value = Math.round(
        (progressEvent.loaded * 100) / progressEvent.total
      )
    }

    const { data, isReady, isLoading, execute } = useHttp({
      method: 'POST',
      onUploadProgress,
    })

    const updateOrder = (
      payload,
      successCallback = () => {},
      errorCallback = () => {}
    ) => {
      uploadingProgress.value = 0

      const isWritingService =
        orderStore.selectedOrderType === OrderTypes.WritingService
      const isRedeemedCredit =
        orderStore.orderType === OrderTypes.RedeemedCredit
      const unSelected = orderStore.selectedOrderType === OrderTypes.UnSelected

      const getOrderType = () => {
        if (unSelected) return null
        if (isRedeemedCredit) return OrderTypes.RedeemedCredit
        if (isWritingService) return OrderTypes.Normal

        return orderStore.selectedOrderType
      }

      const orderType = getOrderType()

      let apiPayload = {
        orderType,
        ...payload,
      }

      if (isWritingService) {
        apiPayload['writing'] = 1
      }

      if (
        (orderType === OrderTypes.Normal && !isWritingService) ||
        orderType === OrderTypes.CreditOnly
      ) {
        apiPayload['writing'] = 0
      }

      if (orderType === OrderTypes.RedeemedCredit && !isWritingService) {
        apiPayload['writing'] = 0
      }

      if (orderType === OrderTypes.RedeemedCredit && orderStore.isWriting) {
        apiPayload['writing'] = 1
      }

      if (
        orderType === OrderTypes.RedeemedCredit &&
        orderStore.selectedOrderType === OrderTypes.Normal
      ) {
        apiPayload['writing'] = 0
      }

      const snakelizedPayload = camelToSnake(apiPayload)

      const formData = buildFormDataFrom(
        removeNullableFieldsFrom(snakelizedPayload)
      )

      execute(
        {
          url: API.updateOrderResource.replace(':id', orderId),
          payload: formData,
        },
        () => {
          order.value = data.value
          orderStore.setOrder(data.value)
          successCallback(data.value)
        },
        () => errorCallback()
      )
    }
    return { data, isReady, isLoading, execute, updateOrder, uploadingProgress }
  }

  function clearOrder() {
    order.value = null
  }

  return {
    order,
    orderTotalPrice,
    redeemedOrderWithZeroTotal,
    zeroTotal,
    clearOrder,
    activeStep,
    getOrderById,
    getAllOrders,
    getOrderId,
    getLastIncompleteOrder,
    useUpdateOrder,
  }
}

export { order, useOrder }
